import { useParams, NavLink } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const category = article ? categories.find((c) => c.slug === article.category) : null;

  const relatedArticles = article
    ? articles
        .filter((a) => a.slug !== article.slug && a.category === article.category)
        .slice(0, 3)
    : [];

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-2xl text-foreground-300"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您要查看的文章不存在或已被移除。</p>
            <NavLink
              to="/"
              className="inline-flex items-center px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              返回首页
            </NavLink>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: category?.name || '分类', href: `/category/${article.category}` },
    { label: article.title },
  ];

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '草莓色研社',
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      item: item.href ? `https://strawberrycolor.cn${item.href}` : undefined,
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="px-4 md:px-6 py-8">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />

              <article>
                <div className="mb-8">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-accent-500 text-background-50">
                      {category?.name}
                    </span>
                    <span className="text-xs text-foreground-400">{article.date}</span>
                    <span className="text-xs text-foreground-400">·</span>
                    <span className="text-xs text-foreground-400">阅读 {article.readTime}</span>
                  </div>

                  <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-4">
                    {article.title}
                  </h1>

                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-accent-100 flex items-center justify-center">
                      <i className="ri-user-line text-accent-600 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{article.author}</p>
                      <p className="text-xs text-foreground-400">草莓色研社 · 认证创作者</p>
                    </div>
                  </div>
                </div>

                <div className="mb-8 rounded-lg overflow-hidden">
                  <img
                    src={article.imageUrl}
                    alt={article.title}
                    className="w-full h-64 md:h-96 object-cover"
                  />
                </div>

                <div
                  className="prose prose-strawberry max-w-none"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />

                <div className="mt-10 pt-8 border-t border-background-200/70">
                  <div className="flex flex-wrap gap-2">
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-800"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              </article>

              {relatedArticles.length > 0 && (
                <section className="mt-14 pt-10 border-t border-background-200/70">
                  <h3 className="text-xl font-heading font-bold text-foreground-950 mb-6">
                    相关文章
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                    {relatedArticles.map((ra) => (
                      <NavLink
                        key={ra.slug}
                        to={`/article/${ra.slug}`}
                        className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200/60 transition-all duration-300 cursor-pointer"
                      >
                        <div className="relative overflow-hidden">
                          <img
                            src={ra.imageUrl}
                            alt={ra.title}
                            className="w-full h-40 object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                        <div className="p-4">
                          <h4 className="text-sm font-semibold text-foreground-950 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                            {ra.title}
                          </h4>
                          <p className="text-xs text-foreground-400 mt-2">{ra.readTime}</p>
                        </div>
                      </NavLink>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}